// Listen buttons for the sentence analyser: a pill on the dark sentence card (with a slow option) and a round icon
// button on each word card. Like <AudioButton>, they play a teacher's recording when one exists, otherwise the device
// voice, and render nothing when neither can play the text.

import type { FC, ReactNode } from 'react'

import { Turtle, Volume2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { audioKey, useAudio } from '@/prakrit/lib/client/audio'

interface ListenButtonProps {
    text: string
    /** dark: pill for the navy sentence card. icon: round button for a word card. */
    tone: 'dark' | 'icon'
    className?: string
}

export const ListenButton: FC<ListenButtonProps> = ({ text, tone, className }): ReactNode => {
    const { ready, source, play, playing } = useAudio()
    const key = audioKey(text)

    if (!ready) return null

    const from = source(key)

    if (!from) return null

    const title = from === 'recording'
        ? 'शिक्षक की रिकॉर्डिंग · Teacher recording'
        : 'उपकरण की आवाज़, लगभग उच्चारण · Device voice, approximate pronunciation'

    if (tone === 'icon') {
        return (
            <button
                type='button'
                onClick={() => play(key, 'normal')}
                aria-pressed={playing === key}
                title={title}
                className={cn(
                    'inline-flex size-tap shrink-0 items-center justify-center rounded-full border border-border bg-card text-foreground transition-colors hover:border-saffron/60 hover:bg-saffron/5 aria-pressed:border-saffron aria-pressed:bg-saffron/10',
                    className
                )}
            >
                <Volume2 className='size-5' aria-hidden='true' />
                <span className='sr-only'>सुनें · Listen to {text}</span>
            </button>
        )
    }

    return (
        <span className={cn('flex shrink-0 items-center gap-2', className)}>
            <button
                type='button'
                onClick={() => play(key, 'normal')}
                aria-pressed={playing === key}
                title={title}
                className='inline-flex min-h-tap items-center gap-2 rounded-full border border-background/20 bg-background/10 px-4 text-sm font-semibold text-background transition-colors hover:bg-background/20'
            >
                <Volume2 className='size-5' aria-hidden='true' />
                <span lang='hi' className='hindi'>सुनें</span>
                <span aria-hidden='true' className='opacity-60'>·</span>
                <span lang='en'>Listen</span>
            </button>
            <button
                type='button'
                onClick={() => play(key, 'slow')}
                title={title}
                className='inline-flex size-tap items-center justify-center rounded-full border border-background/20 bg-background/10 text-background transition-colors hover:bg-background/20'
            >
                <Turtle className='size-5' aria-hidden='true' />
                <span className='sr-only'>धीरे सुनें · Listen slowly</span>
            </button>
        </span>
    )
}
